import type {
  KeywordResearchHistoryClusterDetail,
  KeywordResearchHistoryDetail,
} from '@/services/keywordResearchHistoryService';

export interface KeywordResearchClusterComparison {
  cluster_name: string;
  is_new_cluster: boolean;
  is_changed_cluster: boolean;
  current_keyword_count: number;
  previous_keyword_count: number;
  new_keyword_count: number;
  retained_keyword_count: number;
  removed_keyword_count: number;
  new_keywords: string[];
  retained_keywords: string[];
}

export interface KeywordResearchRunComparison {
  previous_run_id: string;
  previous_run_created_at: string;
  new_cluster_count: number;
  changed_cluster_count: number;
  unchanged_cluster_count: number;
  new_keyword_count: number;
  cluster_by_name: Record<string, KeywordResearchClusterComparison>;
}

export function normalizeKeywordResearchText(value: string): string {
  return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

function keywordSet(cluster: KeywordResearchHistoryClusterDetail): Set<string> {
  return new Set(
    cluster.keywords
      .map((keyword) => normalizeKeywordResearchText(keyword.keyword))
      .filter(Boolean),
  );
}

function indexClusters(
  run: KeywordResearchHistoryDetail,
): Map<string, KeywordResearchHistoryClusterDetail> {
  return new Map(
    run.clusters.map((cluster) => [
      normalizeKeywordResearchText(cluster.cluster_name),
      cluster,
    ]),
  );
}

export function buildKeywordResearchRunComparison(
  current: KeywordResearchHistoryDetail,
  previous: KeywordResearchHistoryDetail | null | undefined,
): KeywordResearchRunComparison | null {
  if (!previous) return null;

  const previousClusters = indexClusters(previous);
  const clusterByName: Record<string, KeywordResearchClusterComparison> = {};
  let newClusterCount = 0;
  let changedClusterCount = 0;
  let unchangedClusterCount = 0;
  let newKeywordCount = 0;

  current.clusters.forEach((cluster) => {
    const key = normalizeKeywordResearchText(cluster.cluster_name);
    const previousCluster = previousClusters.get(key);
    const currentKeywords = keywordSet(cluster);

    if (!previousCluster) {
      const newKeywords = [...currentKeywords];
      clusterByName[key] = {
        cluster_name: cluster.cluster_name,
        is_new_cluster: true,
        is_changed_cluster: false,
        current_keyword_count: currentKeywords.size,
        previous_keyword_count: 0,
        new_keyword_count: currentKeywords.size,
        retained_keyword_count: 0,
        removed_keyword_count: 0,
        new_keywords: newKeywords,
        retained_keywords: [],
      };
      newClusterCount += 1;
      newKeywordCount += newKeywords.length;
      return;
    }

    const previousKeywords = keywordSet(previousCluster);
    const newKeywords = [...currentKeywords].filter(
      (keyword) => !previousKeywords.has(keyword),
    );
    const retainedKeywords = [...currentKeywords].filter((keyword) =>
      previousKeywords.has(keyword),
    );
    const removedKeywordCount = [...previousKeywords].filter(
      (keyword) => !currentKeywords.has(keyword),
    ).length;
    const isChangedCluster =
      newKeywords.length > 0 || removedKeywordCount > 0;

    clusterByName[key] = {
      cluster_name: cluster.cluster_name,
      is_new_cluster: false,
      is_changed_cluster: isChangedCluster,
      current_keyword_count: currentKeywords.size,
      previous_keyword_count: previousKeywords.size,
      new_keyword_count: newKeywords.length,
      retained_keyword_count: retainedKeywords.length,
      removed_keyword_count: removedKeywordCount,
      new_keywords: newKeywords,
      retained_keywords: retainedKeywords,
    };

    if (isChangedCluster) {
      changedClusterCount += 1;
    } else {
      unchangedClusterCount += 1;
    }
    newKeywordCount += newKeywords.length;
  });

  return {
    previous_run_id: previous.id,
    previous_run_created_at: previous.created_at,
    new_cluster_count: newClusterCount,
    changed_cluster_count: changedClusterCount,
    unchanged_cluster_count: unchangedClusterCount,
    new_keyword_count: newKeywordCount,
    cluster_by_name: clusterByName,
  };
}
